import { createSelector } from '@ngrx/store';
import {
  ActionTypes,
  TypeAction
} from './apply-form.actions';
import { IRootState } from '../../store';
import { IOption } from '../../shared/components/select';
import { ISelectOptionModel } from '../../core/services/interfaces';
import { ValidationTypesEnum } from '../../core/services/employee/employee.service';

export enum FieldsAsyncValidationEnum {
  phoneNumber
}

export interface IFieldsLoadingProgress {
  phoneNumber: boolean;
}

export interface FormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  school: string;
  photo: string | null;
  complete: boolean;
}

export interface Captcha {
  touched: boolean;
  verified: boolean;
  value: string | null;
}

export interface SchoolsDropdown {
  list: ISelectOptionModel[];
  query: string;
}

export interface State {
  captcha: Captcha;
  formData: FormData;
  socialAuth: boolean;
  schools: SchoolsDropdown;
  phonesValidity: {
    phoneNumber: ValidationTypesEnum;
  };
  fieldsLoadingProgress: IFieldsLoadingProgress;
}

const initialState = {
  formData: {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    school: '',
    photo: null,
    complete: false
  },
  socialAuth: false,
  captcha: {
    verified: false,
    touched: false,
    value: null
  },
  schools: {
    list: [],
    query: ''
  },
  phonesValidity: {
    phoneNumber: ValidationTypesEnum.initial
  },
  fieldsLoadingProgress: {
    phoneNumber: false
  }
};

export function applyFormReducer(state: State = initialState, action: TypeAction): State {
  const { type, payload } = action;

  switch (type) {
    case ActionTypes.INPUT_UPDATE: {
      const { formData } = state;
      const result = Object.assign({}, formData, {
        [payload.fieldId]: payload.value
      });

      return Object.assign({}, state, { formData: result });
    }

    case ActionTypes.CAPTCHA_UPDATE: {
      const { captcha } = state;
      const result = Object.assign({}, captcha, payload.value);

      return Object.assign({}, state, { captcha: result });
    }

    case ActionTypes.COMPLETE_FORM: {
      const { formData } = state;
      const result = Object.assign({}, formData, {
        complete: true
      });

      return Object.assign({}, state, { formData: result });
    }

    case ActionTypes.SOCIAL_COMPLETE: {
      const { formData } = state;
      const result = Object.assign({}, formData, payload);

      return Object.assign({}, state, { formData: result, socialAuth: true });
    }

    case ActionTypes.SCHOOLS_LIST_UPDATE: {
      const { schools } = state;
      const result = Object.assign({}, schools, { list: payload });

      return Object.assign({}, state, { schools: result });
    }

    case ActionTypes.SCHOOLS_INPUT_UPDATE: {
      const { schools } = state;
      const result = Object.assign({}, schools, {
        query: payload
      });

      return Object.assign({}, state, { schools: result });
    }

    case ActionTypes.SET_FIELD_LOADING_STATE: {
      const { fieldsLoadingProgress }: State = state;
      const changes = {
        ...fieldsLoadingProgress,
        [FieldsAsyncValidationEnum[payload.field]]: payload.isLoading
      };

      return Object.assign({}, state, { fieldsLoadingProgress: changes });
    }

    case ActionTypes.SET_PHONE_VALIDATION: {
      const { phonesValidity }: State = state;
      const changes = {
        ...phonesValidity,
        [FieldsAsyncValidationEnum[payload.field]]: payload.valid
      };

      return Object.assign({}, state, { phonesValidity: changes });
    }

    default: {
      return state;
    }
  }
}

export const getFormData = createSelector(
  ({ applyForm }: IRootState) => applyForm,
  ({ formData }: State) => formData
);

export const getCaptcha = createSelector(
  ({ applyForm }: IRootState) => applyForm,
  ({ captcha }: State) => captcha
);

export const getSocialAuth = createSelector(
  ({ applyForm }: IRootState) => applyForm,
  ({ socialAuth }: State) => socialAuth
);

export const getSchoolSubmitModel = createSelector(
  ({ applyForm }: IRootState) => applyForm,
  ({ formData, schools: { list } }: State) =>
    list.find((college) =>
      Number(college.value) === Number(formData.school))
);

export const getFilteredColleges = createSelector(
  ({ applyForm }: IRootState) => applyForm,
  (state: State): IOption[] => {
    const {
      schools: { list, query },
    } = state;

    return list
      .filter((school: any) => school.text
        .toLowerCase()
        .includes(query.toLowerCase()))
      .map((school) => ({
        value: school.value,
        label: school.text
      }));
  }
);

export const getIsFieldLoading = (field: FieldsAsyncValidationEnum) => createSelector(
  ({ applyForm }: IRootState) => applyForm,
  ({ fieldsLoadingProgress }: any) => fieldsLoadingProgress[FieldsAsyncValidationEnum[field]]
);
export const getPhoneValidity = (field: FieldsAsyncValidationEnum) => createSelector(
  ({ applyForm }: IRootState) => applyForm,
  ({ phonesValidity }: any) => phonesValidity[FieldsAsyncValidationEnum[field]]
);
